import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SystemTray from "./SystemTray";
import StartMenu from "../startmenu/StartMenu";
import ContextMenu from "../desktop/ContextMenu";
import deerLogo from "../../assets/img/deer-start.png";
import WebtopLayoutContext from "../layout/WebtopLayoutContext";
import { useFilteredSideMenu, getMenuIcon, subRouteOf } from "../sideMenu";
import {
  openWindow,
  focusWindow,
  minimizeWindow,
  closeWindow,
  selectTaskbarWindows,
  selectFocusedId,
  WINDOW_STATE,
} from "../../Slices/windowsSlice";

/**
 * Taskbar — the Windows 11-style taskbar that replaces the proto taskbar.
 *
 * Layout: a centered group (Start button + one button per open window) and a
 * right-aligned system tray (login time, task time, clock, notifications).
 *
 * Window button behavior (Win11):
 *  - click a non-focused / minimized window → focus & restore it
 *  - click the focused window → minimize it
 *
 * The Start button (and Ctrl/Cmd+K) opens the Start menu / global search.
 */
const Taskbar = () => {
  const dispatch = useDispatch();
  const windows = useSelector(selectTaskbarWindows);
  const focusedId = useSelector(selectFocusedId);
  const { layout } = useContext(WebtopLayoutContext);
  // Strictly access-filtered (no dev fallback): pins respect RouteAccess.
  const menu = useFilteredSideMenu();
  const [startOpen, setStartOpen] = useState(false);
  const [ctx, setCtx] = useState(null);

  // Close the Start menu on Escape. (Outside-clicks are handled by the Start
  // menu's own full-screen backdrop, which is portaled to <body>.)
  useEffect(() => {
    if (!startOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setStartOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [startOpen]);

  const onTaskContextMenu = (e, win) => {
    e.preventDefault();
    const isMinimized = win.state === WINDOW_STATE.MINIMIZED;
    setCtx({
      x: e.clientX,
      y: e.clientY,
      items: [
        isMinimized
          ? { label: "Restore", onClick: () => dispatch(focusWindow(win.id)) }
          : { label: "Minimize", onClick: () => dispatch(minimizeWindow(win.id)) },
        { separator: true },
        { label: "Close", danger: true, onClick: () => dispatch(closeWindow(win.id)) },
      ],
    });
  };

  // Pinned apps resolved to their launchable sections — memoized so the
  // map+find+filter doesn't rerun on unrelated renders (e.g. window focus).
  const pins = useMemo(
    () => (layout?.taskbarPins || []).map((name) => menu.find((s) => s.name === name)).filter(Boolean),
    [layout?.taskbarPins, menu]
  );

  const launchPin = (section) => {
    const sub = section.Subfields[0];
    if (!sub) return;
    const path = subRouteOf(sub);
    const existing = windows.find((w) => w.path === path);
    if (existing) dispatch(focusWindow(existing.id));
    else dispatch(openWindow({ path, title: sub.displayName }));
  };

  // Ctrl/Cmd+K toggles the Start menu (Spotlight-style).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setStartOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onTaskClick = (win) => {
    const isMinimized = win.state === WINDOW_STATE.MINIMIZED;
    if (win.id === focusedId && !isMinimized) {
      dispatch(minimizeWindow(win.id));
    } else {
      dispatch(focusWindow(win.id)); // focusWindow also restores a minimized window
    }
  };

  return (
    <div className="webtop-taskbar">
      <StartMenu open={startOpen} onClose={() => setStartOpen(false)} />

      <div className="webtop-taskbar__center">
        <button
          type="button"
          className={`webtop-start${startOpen ? " webtop-start--open" : ""}`}
          title="Start (Ctrl/Cmd+K)"
          aria-label="Start"
          onClick={() => setStartOpen((o) => !o)}
        >
          <img
            className="webtop-start__deer"
            src={deerLogo}
            alt="BP"
            style={{ width: 28, height: 28, objectFit: "contain", display: "block" }}
          />
        </button>

        {pins.map((section) => (
          <button
            key={`pin-${section.name}`}
            type="button"
            className="webtop-task webtop-task--pin"
            title={`${section.name} (pinned)`}
            aria-label={`${section.name} (pinned)`}
            onClick={() => launchPin(section)}
          >
            <span className="webtop-task__glyph">
              <FontAwesomeIcon icon={getMenuIcon(section.name)} />
            </span>
          </button>
        ))}

        {windows.map((win) => {
          const isMinimized = win.state === WINDOW_STATE.MINIMIZED;
          const isActive = win.id === focusedId && !isMinimized;
          const glyph = (win.title || win.appKey || "?").trim().charAt(0).toUpperCase();
          const cls = [
            "webtop-task",
            isActive ? "webtop-task--active" : "",
            isMinimized ? "webtop-task--min" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={win.id}
              type="button"
              className={cls}
              onClick={() => onTaskClick(win)}
              onContextMenu={(e) => onTaskContextMenu(e, win)}
              title={win.title}
            >
              <span className="webtop-task__glyph">{glyph}</span>
              <span className="webtop-task__name">{win.title}</span>
            </button>
          );
        })}
      </div>

      <SystemTray />

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={ctx.items} onClose={() => setCtx(null)} />}
    </div>
  );
};

export default Taskbar;
